interface ConfigItem {
  values: Record<number, string>;
}

interface ConfigGroup {
  items: Record<string, ConfigItem>;
}

interface ChatConfig {
  groups: Record<string, ConfigGroup>;
}

// TO-DO: implement dynamic loading
// Not exported: `getChatConfigValue` at the bottom of this file is the only
// reader, and the only intended one — a caller reaching into the table directly
// would bypass its locale-1 fallback.
const CHAT_CONFIG: ChatConfig = {
  groups: {
    client_ui: {
      items: {
        loading_text: {
          values: {
            1: 'Preparing your conversation...',
            2: 'Preparando su conversación...',
            3: 'Préparation de votre conversation...',
            4: 'Ihre Konversation wird vorbereitet...',
            5: 'Preparazione della conversazione...',
            6: 'Preparando sua conversa...',
            7: 'Uw gesprek voorbereiden...',
            8: 'Подготовка к разговору...',
            9: '正在准备您的对话...',
            10: '会話の準備をしています...',
            11: '대화를 준비하는 중...',
            12: 'جارٍ إعداد محادثتك...',
            13: 'आपकी बातचीत तैयार कर रहे हैं...',
            14: 'Vestluse ettevalmistamine...',
            15: 'Valmistellaan keskustelua...',
            16: 'Förbereder din konversation...',
            17: 'Forbereder samtalen din...',
            18: 'Forbereder din samtale...',
            19: 'Příprava konverzace...',
            20: 'Beszélgetés előkészítése...',
            21: 'Przygotowywanie rozmowy...',
            22: 'Se pregătește conversația...',
            23: 'Görüşmeniz hazırlanıyor...',
            24: 'กำลังเตรียมการสนทนาของคุณ...',
            25: 'Đang chuẩn bị cuộc trò chuyện...',
            26: 'Menyiapkan percakapan Anda...',
            27: 'Inihahanda ang iyong pag-uusap...',
            28: 'Ruošiamas pokalbis...',
            29: 'Sarunas sagatavošana...',
            30: 'Подготовка на вашия разговор...',
            31: 'Príprava konverzácie...',
            32: 'Priprava vašega pogovora...',
            33: 'Priprema vašeg razgovora...',
            34: 'Припрема вашег разговора...',
            35: 'Підготовка вашої розмови...',
            36: 'Подготовка на вашиот разговор...',
            37: 'Priprema vašeg razgovora...',
            38: 'Përgatitja e bisedës suaj...',
            39: 'Preparando sua conversa...',
          },
        },
        connection_error: {
          values: {
            1: 'Connection timed out. The conversation could not be established.',
            2: 'Tiempo de conexión agotado. No se pudo establecer la conversación.',
            3: 'Délai de connexion dépassé. La conversation n’a pas pu être établie.',
            4: 'Zeitüberschreitung der Verbindung. Die Konversation konnte nicht aufgebaut werden.',
            5: 'Connessione scaduta. Impossibile stabilire la conversazione.',
            6: 'Tempo de conexão esgotado. A conversa não pôde ser estabelecida.',
            7: 'Verbindingstime-out. Het gesprek kon niet tot stand worden gebracht.',
            8: 'Время ожидания соединения истекло. Разговор не удалось установить.',
            9: '连接超时。无法建立对话。',
            10: '接続がタイムアウトしました。会話を確立できませんでした。',
            11: '연결 시간이 초과되었습니다. 대화를 설정할 수 없습니다.',
            12: 'انتهت مهلة الاتصال. تعذر إنشاء المحادثة.',
            13: 'कनेक्शन का समय समाप्त हो गया। बातचीत स्थापित नहीं की जा सकी।',
            14: 'Ühendus aegus. Vestlust ei saanud luua.',
            15: 'Yhteys aikakatkaistiin. Keskustelua ei voitu muodostaa.',
            16: 'Anslutningen gjorde timeout. Konversationen kunde inte upprättas.',
            17: 'Tilkobling ble tidsavbrutt. Samtalen kunne ikke opprettes.',
            18: 'Forbindelsen fik timeout. Samtalen kunne ikke etableres.',
            19: 'Časový limit připojení vypršel. Konverzaci se nepodařilo navázat.',
            20: 'A kapcsolat időtúllépése. A beszélgetést nem sikerült létrehozni.',
            21: 'Limit czasu połączenia minął. Nie udało się nawiązać rozmowy.',
            22: 'Conexiunea a expirat. Conversația nu a putut fi stabilită.',
            23: 'Bağlantı zaman aşımına uğradı. Görüşme kurulamadı.',
            24: 'การเชื่อมต่อหมดเวลา ไม่สามารถสร้างการสนทนาได้',
            25: 'Kết nối đã hết thời gian. Không thể thiết lập cuộc trò chuyện.',
            26: 'Waktu koneksi habis. Percakapan tidak dapat dibuat.',
            27: 'Nag-time out ang koneksyon. Hindi naitatag ang pag-uusap.',
            28: 'Baigėsi ryšio laikas. Pokalbio nepavyko užmegzti.',
            29: 'Savienojuma noildze. Sarunu nevarēja izveidot.',
            30: 'Връзката изтече. Разговорът не можа да бъде установен.',
            31: 'Časový limit pripojenia vypršal. Konverzáciu sa nepodarilo nadviazať.',
            32: 'Povezava je potekla. Pogovora ni bilo mogoče vzpostaviti.',
            33: 'Vrijeme veze je isteklo. Razgovor nije bilo moguće uspostaviti.',
            34: 'Време везе је истекло. Разговор није било могуће успоставити.',
            35: "Час очікування з'єднання минув. Не вдалося встановити розмову.",
            36: 'Времето за поврзување истече. Разговорот не можеше да се воспостави.',
            37: 'Vrijeme veze je isteklo. Razgovor nije bilo moguće uspostaviti.',
            38: 'Koha e lidhjes mbaroi. Biseda nuk mund të krijohej.',
            39: 'Tempo esgotado na conexão. Não foi possível estabelecer a conversa.',
          },
        },
        error_title: {
          values: {
            1: 'Something went wrong',
            2: 'Algo salió mal',
            3: 'Une erreur est survenue',
            4: 'Etwas ist schiefgelaufen',
            5: 'Qualcosa è andato storto',
            6: 'Algo deu errado',
            7: 'Er is iets misgegaan',
            8: 'Что-то пошло не так',
            9: '出错了',
            10: '問題が発生しました',
            11: '문제가 발생했습니다',
            12: 'حدث خطأ ما',
            13: 'कुछ गलत हो गया',
            14: 'Midagi läks valesti',
            15: 'Jokin meni pieleen',
            16: 'Något gick fel',
            17: 'Noe gikk galt',
            18: 'Noget gik galt',
            19: 'Něco se pokazilo',
            20: 'Valami hiba történt',
            21: 'Coś poszło nie tak',
            22: 'Ceva nu a mers bine',
            23: 'Bir şeyler ters gitti',
            24: 'มีบางอย่างผิดพลาด',
            25: 'Đã xảy ra lỗi',
            26: 'Ada yang salah',
            27: 'May nangyaring mali',
            28: 'Kažkas ne taip',
            29: 'Kaut kas nogāja greizi',
            30: 'Нещо се обърка',
            31: 'Niečo sa pokazilo',
            32: 'Nekaj je šlo narobe',
            33: 'Nešto je pošlo po zlu',
            34: 'Нешто је пошло по злу',
            35: 'Щось пішло не так',
            36: 'Нешто тргна наопаку',
            37: 'Nešto je pošlo po zlu',
            38: 'Diçka shkoi keq',
            39: 'Algo deu errado',
          },
        },
        error_description: {
          values: {
            1: 'Unable to set up your conversation. Please try again.',
            2: 'No se pudo configurar su conversación. Inténtelo de nuevo.',
            3: 'Impossible de configurer votre conversation. Veuillez réessayer.',
            4: 'Ihre Konversation konnte nicht eingerichtet werden. Bitte versuchen Sie es erneut.',
            5: 'Impossibile configurare la conversazione. Riprova.',
            6: 'Não foi possível configurar sua conversa. Tente novamente.',
            7: 'Kan uw gesprek niet opzetten. Probeer het opnieuw.',
            8: 'Не удалось настроить разговор. Пожалуйста, попробуйте еще раз.',
            9: '无法设置您的对话。请重试。',
            10: '会話を設定できません。もう一度お試しください。',
            11: '대화를 설정할 수 없습니다. 다시 시도해 주세요.',
            12: 'تعذر إعداد محادثتك. حاول مرة اخرى.',
            13: 'आपकी बातचीत सेट अप करने में असमर्थ। कृपया पुनः प्रयास करें।',
            14: 'Vestlust ei õnnestunud seadistada. Palun proovi uuesti.',
            15: 'Keskustelua ei voitu määrittää. Yritä uudelleen.',
            16: 'Kunde inte konfigurera din konversation. Försök igen.',
            17: 'Kan ikke sette opp samtalen din. Vennligst prøv igjen.',
            18: 'Kunne ikke opsætte din samtale. Prøv venligst igen.',
            19: 'Nepodařilo se nastavit konverzaci. Zkuste to prosím znovu.',
            20: 'Nem sikerült beállítani a beszélgetést. Kérjük, próbálja újra.',
            21: 'Nie można skonfigurować rozmowy. Spróbuj ponownie.',
            22: 'Nu s-a putut configura conversația. Vă rugăm să încercați din nou.',
            23: 'Görüşmeniz ayarlanamadı. Lütfen tekrar deneyin.',
            24: 'ไม่สามารถตั้งค่าการสนทนาของคุณ โปรดลองอีกครั้ง',
            25: 'Không thể thiết lập cuộc trò chuyện. Vui lòng thử lại.',
            26: 'Tidak dapat mengatur percakapan Anda. Silakan coba lagi.',
            27: 'Hindi mai-set up ang iyong pag-uusap. Pakisubukang muli.',
            28: 'Nepavyko nustatyti pokalbio. Bandykite dar kartą.',
            29: 'Nevarēja iestatīt sarunu. Lūdzu, mēģiniet vēlreiz.',
            30: 'Разговорът не може да бъде настроен. Моля, опитайте отново.',
            31: 'Nepodarilo sa nastaviť konverzáciu. Skúste to prosím znova.',
            32: 'Pogovora ni bilo mogoče nastaviti. Poskusite znova.',
            33: 'Nije moguće postaviti vaš razgovor. Molimo pokušajte ponovo.',
            34: 'Није могуће поставити ваш разговор. Покушајте поново.',
            35: 'Не вдалося налаштувати розмову. Будь ласка, спробуйте ще раз.',
            36: 'Не може да се постави вашиот разговор. Ве молиме обидете се повторно.',
            37: 'Nije moguće postaviti vaš razgovor. Molimo pokušajte ponovo.',
            38: 'Nuk mund të konfigurohej biseda juaj. Ju lutemi provoni përsëri.',
            39: 'Não foi possível configurar sua conversa. Por favor, tente novamente.',
          },
        },
        messages_error: {
          values: {
            1: 'Failed to load messages.',
            2: 'Error al cargar los mensajes.',
            3: 'Échec du chargement des messages.',
            4: 'Nachrichten konnten nicht geladen werden.',
            5: 'Impossibile caricare i messaggi.',
            6: 'Falha ao carregar mensagens.',
            7: 'Kan berichten niet laden.',
            8: 'Не удалось загрузить сообщения.',
            9: '无法加载消息。',
            10: 'メッセージを読み込めませんでした。',
            11: '메시지를 로드하지 못했습니다.',
            12: 'فشل تحميل الرسائل.',
            13: 'संदेश लोड करने में विफल।',
            14: 'Sõnumite laadimine ebaõnnestus.',
            15: 'Viestien lataaminen epäonnistui.',
            16: 'Kunde inte ladda meddelanden.',
            17: 'Kunne ikke laste meldinger.',
            18: 'Kunne ikke indlæse beskeder.',
            19: 'Nepodařilo se načíst zprávy.',
            20: 'Nem sikerült betölteni az üzeneteket.',
            21: 'Nie udało się załadować wiadomości.',
            22: 'Nu s-au putut încărca mesajele.',
            23: 'Mesajlar yüklenemedi.',
            24: 'โหลดข้อความไม่สำเร็จ',
            25: 'Không thể tải tin nhắn.',
            26: 'Gagal memuat pesan.',
            27: 'Nabigo ang pag-load ng mga mensahe.',
            28: 'Nepavyko įkelti žinučių.',
            29: 'Neizdevās ielādēt ziņas.',
            30: 'Неуспешно зареждане на съобщенията.',
            31: 'Nepodarilo sa načítať správy.',
            32: 'Ni bilo mogoče naložiti sporočil.',
            33: 'Nije uspjelo učitavanje poruka.',
            34: 'Није успело учитавање порука.',
            35: 'Не вдалося завантажити повідомлення.',
            36: 'Не успеа да се вчитаат пораките.',
            37: 'Nije uspjelo učitavanje poruka.',
            38: 'Dështoi ngarkimi i mesazheve.',
            39: 'Falha ao carregar mensagens.',
          },
        },
        reload_button: {
          values: {
            1: 'Reload',
            2: 'Recargar',
            3: 'Recharger',
            4: 'Neu laden',
            5: 'Ricarica',
            6: 'Recarregar',
            7: 'Opnieuw laden',
            8: 'Перезагрузить',
            9: '重新加载',
            10: '再読み込み',
            11: '새로 고침',
            12: 'إعادة تحميل',
            13: 'पुनः लोड करें',
            14: 'Laadi uuesti',
            15: 'Lataa uudelleen',
            16: 'Ladda om',
            17: 'Last inn på nytt',
            18: 'Genindlæs',
            19: 'Načíst znovu',
            20: 'Újratöltés',
            21: 'Przeładuj',
            22: 'Reîncărcare',
            23: 'Yeniden Yükle',
            24: 'โหลดซ้ำ',
            25: 'Tải lại',
            26: 'Muat ulang',
            27: 'I-reload',
            28: 'Įkelti iš naujo',
            29: 'Pārlādēt',
            30: 'Презареди',
            31: 'Načítať znova',
            32: 'Ponovno naloži',
            33: 'Ponovno učitaj',
            34: 'Поново учитај',
            35: 'Перезавантажити',
            36: 'Вчитај повторно',
            37: 'Ponovno učitaj',
            38: 'Ringarko',
            39: 'Recarregar',
          },
        },
        loading_title: {
          values: {
            1: 'Please wait',
            2: 'Por favor espere',
            3: 'Veuillez patienter',
            4: 'Bitte warten',
            5: 'Attendere prego',
            6: 'Por favor aguarde',
            7: 'Een ogenblik geduld',
            8: 'Пожалуйста, подождите',
            9: '请稍候',
            10: 'お待ちください',
            11: '기다려 주세요',
            12: 'رجاء انتظر',
            13: 'कृपया प्रतीक्षा करें',
            14: 'Palun oota',
            15: 'Odota',
            16: 'Vänta',
            17: 'Vent litt',
            18: 'Vent venligst',
            19: 'Prosím, čekejte',
            20: 'Kérjük, várjon',
            21: 'Proszę czekać',
            22: 'Vă rugăm să așteptați',
            23: 'Lütfen bekleyin',
            24: 'โปรดรอสักครู่',
            25: 'Vui lòng đợi',
            26: 'Mohon tunggu',
            27: 'Mangyaring maghintay',
            28: 'Prašome palaukti',
            29: 'Lūdzu, uzgaidiet',
            30: 'Моля изчакайте',
            31: 'Prosím čakajte',
            32: 'Prosim počakajte',
            33: 'Molimo pričekajte',
            34: 'Молимо сачекајте',
            35: 'Будь ласка, зачекайте',
            36: 'Ве молиме почекајте',
            37: 'Molimo pričekajte',
            38: 'Ju lutem prisni',
            39: 'Por favor, aguarde',
          },
        },
        input_placeholder: {
          values: {
            1: 'Type your message...',
            2: 'Escribe tu mensaje...',
            3: 'Tapez votre message...',
            4: 'Geben Sie Ihre Nachricht ein...',
            5: 'Scrivi il tuo messaggio...',
            6: 'Digite sua mensagem...',
            7: 'Typ uw bericht...',
            8: 'Введите ваше сообщение...',
            9: '输入您的消息...',
            10: 'メッセージを入力...',
            11: '메시지를 입력하세요...',
            12: 'اكتب رسالتك...',
            13: 'अपना संदेश लिखें...',
            14: 'Sisestage oma sõnum...',
            15: 'Kirjoita viestisi...',
            16: 'Skriv ditt meddelande...',
            17: 'Skriv meldingen din...',
            18: 'Skriv din besked...',
            19: 'Napište svou zprávu...',
            20: 'Írja be üzenetét...',
            21: 'Wpisz wiadomość...',
            22: 'Scrieți mesajul...',
            23: 'Mesajınızı yazın...',
            24: 'พิมพ์ข้อความของคุณ...',
            25: 'Nhập tin nhắn của bạn...',
            26: 'Ketik pesan Anda...',
            27: 'I-type ang iyong mensahe...',
            28: 'Įveskite savo žinutę...',
            29: 'Ierakstiet ziņojumu...',
            30: 'Въведете вашето съобщение...',
            31: 'Napíšte správu...',
            32: 'Vnesite sporočilo...',
            33: 'Unesite svoju poruku...',
            34: 'Унесите своју поруку...',
            35: 'Введіть ваше повідомлення...',
            36: 'Внесете ја вашата порака...',
            37: 'Unesite svoju poruku...',
            38: 'Shkruani mesazhin tuaj...',
            39: 'Digite sua mensagem...',
          },
        },
        input_attach: {
          values: {
            1: 'Attach file',
            2: 'Adjuntar archivo',
            3: 'Joindre un fichier',
            4: 'Datei anhängen',
            5: 'Allega file',
            6: 'Anexar arquivo',
            7: 'Bestand bijvoegen',
            8: 'Прикрепить файл',
            9: '附加文件',
            10: 'ファイルを添付',
            11: '파일 첨부',
            12: 'إرفاق ملف',
            13: 'फ़ाइल संलग्न करें',
            14: 'Manusta fail',
            15: 'Liitä tiedosto',
            16: 'Bifoga fil',
            17: 'Legg ved fil',
            18: 'Vedhæft fil',
            19: 'Přiložit soubor',
            20: 'Fájl csatolása',
            21: 'Załącz plik',
            22: 'Atașați fișier',
            23: 'Dosya ekle',
            24: 'แนบไฟล์',
            25: 'Đính kèm tệp',
            26: 'Lampirkan file',
            27: 'Maglakip ng file',
            28: 'Prisegti failą',
            29: 'Pievienot failu',
            30: 'Прикачи файл',
            31: 'Priložiť súbor',
            32: 'Priloži datoteko',
            33: 'Priloži datoteku',
            34: 'Приложи датотеку',
            35: 'Прикріпити файл',
            36: 'Прикачи датотека',
            37: 'Priloži datoteku',
            38: 'Bashkangjit skedarin',
            39: 'Anexar arquivo',
          },
        },
        input_camera: {
          values: {
            1: 'Take photo',
            2: 'Tomar foto',
            3: 'Prendre une photo',
            4: 'Foto aufnehmen',
            5: 'Scatta foto',
            6: 'Tirar foto',
            7: 'Maak foto',
            8: 'Сделать фото',
            9: '拍照',
            10: '写真を撮る',
            11: '사진 찍기',
            12: 'التقاط صورة',
            13: 'फोटो लें',
            14: 'Tee pilt',
            15: 'Ota kuva',
            16: 'Ta foto',
            17: 'Ta bilde',
            18: 'Tag billede',
            19: 'Pořídit fotku',
            20: 'Fénykép készítése',
            21: 'Zrób zdjęcie',
            22: 'Faceți o poză',
            23: 'Fotoğraf çek',
            24: 'ถ่ายภาพ',
            25: 'Chụp ảnh',
            26: 'Ambil foto',
            27: 'Kumuha ng larawan',
            28: 'Fotografuoti',
            29: 'Uzņemt foto',
            30: 'Направи снимка',
            31: 'Odfotiť',
            32: 'Slikaj',
            33: 'Uslikaj',
            34: 'Усликај',
            35: 'Зробити фото',
            36: 'Сликај',
            37: 'Uslikaj',
            38: 'Bëj foto',
            39: 'Tirar foto',
          },
        },
        input_send: {
          values: {
            1: 'Send message',
            2: 'Enviar mensaje',
            3: 'Envoyer le message',
            4: 'Nachricht senden',
            5: 'Invia messaggio',
            6: 'Enviar mensagem',
            7: 'Bericht versturen',
            8: 'Отправить сообщение',
            9: '发送消息',
            10: 'メッセージを送信',
            11: '메시지 보내기',
            12: 'إرسال رسالة',
            13: 'संदेश भेजें',
            14: 'Saada sõnum',
            15: 'Lähetä viesti',
            16: 'Skicka meddelande',
            17: 'Send melding',
            18: 'Send besked',
            19: 'Odeslat zprávu',
            20: 'Üzenet küldése',
            21: 'Wyślij wiadomość',
            22: 'Trimite mesaj',
            23: 'Mesaj gönder',
            24: 'ส่งข้อความ',
            25: 'Gửi tin nhắn',
            26: 'Kirim pesan',
            27: 'Ipadala ang mensahe',
            28: 'Siųsti žinutę',
            29: 'Sūtīt ziņu',
            30: 'Изпрати съобщение',
            31: 'Odoslať správu',
            32: 'Pošlji sporočilo',
            33: 'Pošalji poruku',
            34: 'Пошаљи поруку',
            35: 'Надіслати повідомлення',
            36: 'Испрати порака',
            37: 'Pošalji poruku',
            38: 'Dërgo mesazh',
            39: 'Enviar mensagem',
          },
        },
        input_mic: {
          values: {
            1: 'Voice message',
            2: 'Mensaje de voz',
            3: 'Message vocal',
            4: 'Sprachnachricht',
            5: 'Messaggio vocale',
            6: 'Mensagem de voz',
            7: 'Spraakbericht',
            8: 'Голосовое сообщение',
            9: '语音消息',
            10: '音声メッセージ',
            11: '음성 메시지',
            12: 'رسالة صوتية',
            13: 'आवाज़ संदेश',
            14: 'Häälsõnum',
            15: 'Ääniviesti',
            16: 'Röstmeddelande',
            17: 'Talemelding',
            18: 'Talebesked',
            19: 'Hlasová zpráva',
            20: 'Hangüzenet',
            21: 'Wiadomość głosowa',
            22: 'Mesaj vocal',
            23: 'Sesli mesaj',
            24: 'ข้อความเสียง',
            25: 'Tin nhắn thoại',
            26: 'Pesan suara',
            27: 'Mensahe ng boses',
            28: 'Balso žinutė',
            29: 'Balss ziņa',
            30: 'Гласово съобщение',
            31: 'Hlasová správa',
            32: 'Glasovno sporočilo',
            33: 'Glasovna poruka',
            34: 'Гласовна порука',
            35: 'Голосове повідомлення',
            36: 'Гласовна порака',
            37: 'Glasovna poruka',
            38: 'Mesazh zanor',
            39: 'Mensagem de voz',
          },
        },
        input_recording: {
          values: {
            1: 'Recording...',
            2: 'Grabando...',
            3: 'Enregistrement...',
            4: 'Aufnahme...',
            5: 'Registrazione...',
            6: 'Gravando...',
            7: 'Opnemen...',
            8: 'Запись...',
            9: '正在录音...',
            10: '録音中...',
            11: '녹음 중...',
            12: 'جاري التسجيل...',
            13: 'रिकॉर्डिंग...',
            14: 'Salvestamine...',
            15: 'Tallennetaan...',
            16: 'Spelar in...',
            17: 'Tar opp...',
            18: 'Optager...',
            19: 'Nahrávání...',
            20: 'Felvétel...',
            21: 'Nagrywanie...',
            22: 'Se înregistrează...',
            23: 'Kaydediliyor...',
            24: 'กำลังบันทึก...',
            25: 'Đang ghi âm...',
            26: 'Merekam...',
            27: 'Nagre-record...',
            28: 'Įrašoma...',
            29: 'Ierakati...',
            30: 'Записване...',
            31: 'Nahrávanie...',
            32: 'Snemanje...',
            33: 'Snimanje...',
            34: 'Снимање...',
            35: 'Запис...',
            36: 'Снимање...',
            37: 'Snimanje...',
            38: 'Duke regjistruar...',
            39: 'Gravando...',
          },
        },
        status_connected: {
          values: {
            1: 'Connected',
            2: 'Conectado',
            3: 'Connecté',
            4: 'Verbunden',
            5: 'Connesso',
            6: 'Conectado',
            7: 'Verbonden',
            8: 'Подключено',
            9: '已连接',
            10: '接続済み',
            11: '연결됨',
            12: 'متصل',
            13: 'जुड़ा हुआ',
            14: 'Ühendatud',
            15: 'Yhdistetty',
            16: 'Ansluten',
            17: 'Tilkoblet',
            18: 'Tilsluttet',
            19: 'Připojeno',
            20: 'Kapcsolódva',
            21: 'Połączono',
            22: 'Conectat',
            23: 'Bağlandı',
            24: 'เชื่อมต่อแล้ว',
            25: 'Đã kết nối',
            26: 'Terhubung',
            27: 'Konektado',
            28: 'Prisijungta',
            29: 'Savienots',
            30: 'Свързан',
            31: 'Pripojené',
            32: 'Povezano',
            33: 'Povezano',
            34: 'Повезано',
            35: 'Підключено',
            36: 'Поврзано',
            37: 'Povezano',
            38: 'Lidhur',
            39: 'Conectado',
          },
        },
        status_connecting: {
          values: {
            1: 'Connecting...',
            2: 'Conectando...',
            3: 'Connexion...',
            4: 'Verbinden...',
            5: 'Connessione...',
            6: 'Conectando...',
            7: 'Verbinden...',
            8: 'Подключение...',
            9: '正在连接...',
            10: '接続中...',
            11: '연결 중...',
            12: 'جاري الاتصال...',
            13: 'कनेक्ट कर रहा है...',
            14: 'Ühendamine...',
            15: 'Yhdistetään...',
            16: 'Ansluter...',
            17: 'Kobler til...',
            18: 'Forbinder...',
            19: 'Připojování...',
            20: 'Kapcsolódás...',
            21: 'Łączenie...',
            22: 'Se conectează...',
            23: 'Bağlanıyor...',
            24: 'กำลังเชื่อมต่อ...',
            25: 'Đang kết nối...',
            26: 'Menghubungkan...',
            27: 'Kumokonekta...',
            28: 'Jungiamasi...',
            29: 'Savienojas...',
            30: 'Свързване...',
            31: 'Pripájanie...',
            32: 'Povezovanje...',
            33: 'Povezivanje...',
            34: 'Повезивање...',
            35: 'Підключення...',
            36: 'Повзување...',
            37: 'Povezivanje...',
            38: 'Duke u lidhur...',
            39: 'Conectando...',
          },
        },
        status_offline: {
          values: {
            1: 'Offline',
            2: 'Desconectado',
            3: 'Hors ligne',
            4: 'Offline',
            5: 'Offline',
            6: 'Offline',
            7: 'Offline',
            8: 'Офлайн',
            9: '离线',
            10: 'オフライン',
            11: '오프라인',
            12: 'غير متصل',
            13: 'ऑफ़लाइन',
            14: 'Võrguühenduseta',
            15: 'Offline',
            16: 'Offline',
            17: 'Frakoblet',
            18: 'Offline',
            19: 'Offline',
            20: 'Offline',
            21: 'Offline',
            22: 'Deconectat',
            23: 'Çevrimdışı',
            24: 'ออฟไลน์',
            25: 'Ngoại tuyến',
            26: 'Offline',
            27: 'Offline',
            28: 'Atsijungęs',
            29: 'Bezsaistē',
            30: 'Офлайн',
            31: 'Offline',
            32: 'Brez povezave',
            33: 'Izvan mreže',
            34: 'Ван мреже',
            35: 'Офлайн',
            36: 'Офлајн',
            37: 'Izvan mreže',
            38: 'Jashtë linje',
            39: 'Offline',
          },
        },
        header_options: {
          values: {
            1: 'More options',
            2: 'Más opciones',
            3: "Plus d'options",
            4: 'Mehr Optionen',
            5: 'Altre opzioni',
            6: 'Mais opções',
            7: 'Meer opties',
            8: 'Больше опций',
            9: '更多选项',
            10: 'その他のオプション',
            11: '더 많은 옵션',
            12: 'المزيد من الخيارات',
            13: 'अधिक विकल्प',
            14: 'Rohkem valikuid',
            15: 'Lisää vaihtoehtoja',
            16: 'Fler alternativ',
            17: 'Flere alternativer',
            18: 'Flere muligheder',
            19: 'Více možností',
            20: 'További lehetőségek',
            21: 'Więcej opcji',
            22: 'Mai multe opțiuni',
            23: 'Daha fazla seçenek',
            24: 'ตัวเลือกเพิ่มเติม',
            25: 'Tùy chọn khác',
            26: 'Opsi lainnya',
            27: 'Higit pang mga opsyon',
            28: 'Daugiau parinkčių',
            29: 'Vairāk iespēju',
            30: 'Повече опции',
            31: 'Viac možností',
            32: 'Več možnosti',
            33: 'Više opcija',
            34: 'Више опција',
            35: 'Більше опцій',
            36: 'Повеќе опции',
            37: 'Više opcija',
            38: 'Më shumë opsione',
            39: 'Mais opções',
          },
        },
        header_search: {
          values: {
            1: 'Search in chat',
            2: 'Buscar en el chat',
            3: 'Rechercher dans le chat',
            4: 'Im Chat suchen',
            5: 'Cerca nella chat',
            6: 'Pesquisar no chat',
            7: 'Zoeken in chat',
            8: 'Поиск в чате',
            9: '在聊天中搜索',
            10: 'チャット内を検索',
            11: '채팅에서 검색',
            12: 'بحث في الدردشة',
            13: 'चैट में खोजें',
            14: 'Otsi vestlusest',
            15: 'Etsi keskustelusta',
            16: 'Sök i chatt',
            17: 'Søk i chat',
            18: 'Søg i chat',
            19: 'Hledat v chatu',
            20: 'Keresés a csevegésben',
            21: 'Szukaj w czacie',
            22: 'Căutare în chat',
            23: 'Sohbette ara',
            24: 'ค้นหาในแชท',
            25: 'Tìm kiếm trong cuộc trò chuyện',
            26: 'Cari di obrolan',
            27: 'Maghanap sa chat',
            28: 'Ieškoti pokalbyje',
            29: 'Meklēt tērzēšanā',
            30: 'Търсене в чата',
            31: 'Hľadať v chate',
            32: 'Išči v klepetu',
            33: 'Pretraži u chatu',
            34: 'Претражи у ћаскању',
            35: 'Пошук у чаті',
            36: 'Пребарувај во разговор',
            37: 'Pretraži u chatu',
            38: 'Kërko në bisedë',
            39: 'Pesquisar no chat',
          },
        },
        header_mute: {
          values: {
            1: 'Mute notifications',
            2: 'Silenciar notificaciones',
            3: 'Désactiver les notifications',
            4: 'Benachrichtigungen stummschalten',
            5: 'Disattiva notifiche',
            6: 'Silenciar notificações',
            7: 'Meldingen dempen',
            8: 'Отключить уведомления',
            9: '静音通知',
            10: '通知をミュート',
            11: '알림 음소거',
            12: 'کتم الإشعارات',
            13: 'सूचनाएं म्यूट करें',
            14: 'Vaigista teavitused',
            15: 'Mykistä ilmoitukset',
            16: 'Stäng av aviseringar',
            17: 'Demp varsler',
            18: 'Slå notifikationer fra',
            19: 'Ztlumit oznámení',
            20: 'Értesítések némítása',
            21: 'Wycisz powiadomienia',
            22: 'Opriți notificările',
            23: 'Bildirimleri sessize al',
            24: 'ปิดการแจ้งเตือน',
            25: 'Tắt thông báo',
            26: 'Bisukan notifikasi',
            27: 'I-mute ang mga notification',
            28: 'Išjungti pranešimus',
            29: 'Izslēgt paziņojumus',
            30: 'Изключи известията',
            31: 'Stlmiť upozornenia',
            32: 'Utišaj obvestila',
            33: 'Utišaj obavijesti',
            34: 'Утишај обавештења',
            35: 'Вимкнути сповіщення',
            36: 'Исклучи известувања',
            37: 'Utišaj obavijesti',
            38: 'Hesht njoftimet',
            39: 'Silenciar notificações',
          },
        },
        header_delete: {
          values: {
            1: 'Delete conversation',
            2: 'Eliminar conversación',
            3: 'Supprimer la conversation',
            4: 'Konversation löschen',
            5: 'Elimina conversazione',
            6: 'Excluir conversa',
            7: 'Gesprek verwijderen',
            8: 'Удалить разговор',
            9: '删除对话',
            10: '会話を削除',
            11: '대화 삭제',
            12: 'حذف المحادثة',
            13: 'बातचीत हटाएं',
            14: 'Kustuta vestlus',
            15: 'Poista keskustelu',
            16: 'Ta bort konversation',
            17: 'Slett samtale',
            18: 'Slet samtale',
            19: 'Smazat konverzaci',
            20: 'Beszélgetés törlése',
            21: 'Usuń rozmowę',
            22: 'Ștergeți conversația',
            23: 'Görüşmeyi sil',
            24: 'ลบการสนทนา',
            25: 'Xóa cuộc trò chuyện',
            26: 'Hapus percakapan',
            27: 'Tanggalin ang pag-uusap',
            28: 'Ištrinti pokalbį',
            29: 'Dzēst sarunu',
            30: 'Изтрий разговора',
            31: 'Vymazať konverzáciu',
            32: 'Izbriši pogovor',
            33: 'Izbriši razgovor',
            34: 'Обриши разговор',
            35: 'Видалити розмову',
            36: 'Избриши разговор',
            37: 'Izbriši razgovor',
            38: 'Fshi bisedën',
            39: 'Excluir conversa',
          },
        },
        error_dismiss: {
          values: {
            1: 'Dismiss',
            2: 'Descartar',
            3: 'Rejeter',
            4: 'Verwerfen',
            5: 'Ignora',
            6: 'Dispensar',
            7: 'Afwijzen',
            8: 'Отклонить',
            9: '忽略',
            10: '閉じる',
            11: '닫기',
            12: 'تجاهل',
            13: 'خारिज करें',
            14: 'Loobu',
            15: 'Hylkää',
            16: 'Avfärda',
            17: 'Avvis',
            18: 'Afvis',
            19: 'Zrušit',
            20: 'Elvetés',
            21: 'Odrzuć',
            22: 'Renunță',
            23: 'Kapat',
            24: 'ยกเลิก',
            25: 'Bỏ qua',
            26: 'Tutup',
            27: 'I-dismiss',
            28: 'Atmesti',
            29: 'Noraidīt',
            30: 'Отхвърли',
            31: 'Zrušiť',
            32: 'Opusti',
            33: 'Odbaci',
            34: 'Одбаци',
            35: 'Відхилити',
            36: 'Отфрли',
            37: 'Odbaci',
            38: 'Hiq',
            39: 'Dispensar',
          },
        },
        error_reload: {
          values: {
            1: 'Reload Page',
            2: 'Recargar página',
            3: 'Recharger la page',
            4: 'Seite neu laden',
            5: 'Ricarica pagina',
            6: 'Recarregar página',
            7: 'Pagina herladen',
            8: 'Перезагрузить страницу',
            9: '重新加载页面',
            10: 'ページを再読み込み',
            11: '페이지 새로고침',
            12: 'إعادة تحميل الصفحة',
            13: 'पेज रीलोड करें',
            14: 'Laadi lehekülg uuesti',
            15: 'Lataa sivu uudelleen',
            16: 'Ladda om sidan',
            17: 'Last inn siden på nytt',
            18: 'Genindlæs siden',
            19: 'Znovu načíst stránku',
            20: 'Oldal újratöltése',
            21: 'Przeładuj stronę',
            22: 'Reîncarcă pagina',
            23: 'Sayfayı yenile',
            24: 'โหลดหน้าใหม่',
            25: 'Tải lại trang',
            26: 'Muat ulang halaman',
            27: 'I-reload ang page',
            28: 'Perkrauti puslapį',
            29: 'Pārlādēt lapu',
            30: 'Презареди страницата',
            31: 'Znovu načítať stránku',
            32: 'Ponovno učitaj stranicu',
            33: 'Ponovo učitaj stranicu',
            34: 'Поново учитај страницу',
            35: 'Перезавантажити сторінку',
            36: 'Повторно вчитај страница',
            37: 'Ponovno učitaj stranicu',
            38: 'Rifresho faqen',
            39: 'Recarregar página',
          },
        },
        messages_empty: {
          values: {
            1: 'No messages yet. Start the conversation!',
            39: 'Ainda não há mensagens. Inicie a conversa!',
          },
        },
        degraded_message: {
          values: {
            1: 'We are experiencing issues at the moment. Some replies may be delayed.',
            2: 'Actualmente tenemos problemas técnicos. Algunas respuestas pueden retrasarse.',
            3: 'Nous rencontrons actuellement des problèmes. Certaines réponses peuvent être retardées.',
            4: 'Derzeit treten Probleme auf. Einige Antworten können sich verzögern.',
            5: 'Attualmente stiamo riscontrando problemi. Alcune risposte potrebbero subire ritardi.',
            6: 'Estamos com problemas no momento. Algumas respostas podem atrasar.',
            7: 'We ondervinden momenteel problemen. Sommige antwoorden kunnen vertraging oplopen.',
            8: 'В настоящее время наблюдаются проблемы. Некоторые ответы могут задерживаться.',
            9: '我们目前遇到一些问题。部分回复可能会延迟。',
            10: '現在問題が発生しています。返信が遅れる場合があります。',
            11: '현재 문제가 발생하고 있습니다. 일부 답변이 지연될 수 있습니다.',
            12: 'نواجه حاليًا بعض المشكلات. قد تتأخر بعض الردود.',
            13: 'हमें इस समय कुछ समस्याएं आ रही हैं। कुछ उत्तरों में देरी हो सकती है।',
            14: 'Meil on praegu tõrkeid. Mõned vastused võivad hilineda.',
            15: 'Meillä on tällä hetkellä ongelmia. Jotkin vastaukset voivat viivästyä.',
            16: 'Vi har problem just nu. Vissa svar kan bli fördröjda.',
            17: 'Vi har problemer for tiden. Noen svar kan bli forsinket.',
            18: 'Vi oplever problemer i øjeblikket. Nogle svar kan blive forsinket.',
            19: 'Aktuálně máme potíže. Některé odpovědi mohou být opožděné.',
            20: 'Jelenleg problémákat tapasztalunk. Egyes válaszok késhetnek.',
            21: 'Obecnie występują problemy. Niektóre odpowiedzi mogą być opóźnione.',
            22: 'În prezent avem probleme. Unele răspunsuri pot întârzia.',
            23: 'Şu anda sorunlar yaşıyoruz. Bazı yanıtlar gecikebilir.',
            24: 'ขณะนี้เราพบปัญหาบางอย่าง การตอบกลับบางส่วนอาจล่าช้า',
            25: 'Hiện tại chúng tôi đang gặp sự cố. Một số phản hồi có thể bị chậm.',
            26: 'Saat ini kami mengalami masalah. Beberapa balasan mungkin tertunda.',
            27: 'Kasalukuyan kaming nakakaranas ng problema. Maaaring maantala ang ilang sagot.',
            28: 'Šiuo metu kyla trikdžių. Kai kurie atsakymai gali vėluoti.',
            29: 'Šobrīd mums ir problēmas. Dažas atbildes var aizkavēties.',
            30: 'В момента изпитваме проблеми. Някои отговори може да се забавят.',
            31: 'Momentálne máme problémy. Niektoré odpovede môžu byť oneskorené.',
            32: 'Trenutno imamo težave. Nekateri odgovori se lahko zamaknejo.',
            33: 'Trenutno imamo poteškoća. Neki odgovori mogu biti odgođeni.',
            34: 'Тренутно имамо проблема. Неки одговори могу бити одложени.',
            35: 'Зараз у нас виникли проблеми. Деякі відповіді можуть надходити із затримкою.',
            36: 'Моментално имаме проблеми. Некои одговори може да се одложат.',
            37: 'Trenutno imamo poteškoća. Neki odgovori mogu biti odgođeni.',
            38: 'Aktualisht kemi probleme. Disa përgjigje mund të vonohen.',
            39: 'Estamos com problemas no momento. Algumas respostas podem atrasar.',
          },
        },
        outage_message: {
          values: {
            1: 'We are currently experiencing a service outage. Your conversation may not work until it is resolved. Please try again later.',
            2: 'Actualmente tenemos una interrupción del servicio. Es posible que su conversación no funcione hasta que se resuelva. Inténtelo de nuevo más tarde.',
            3: "Nous rencontrons actuellement une panne de service. Votre conversation risque de ne pas fonctionner jusqu'à sa résolution. Veuillez réessayer plus tard.",
            4: 'Derzeit liegt eine Störung des Dienstes vor. Ihre Konversation funktioniert möglicherweise erst nach der Behebung. Bitte versuchen Sie es später erneut.',
            5: 'Attualmente stiamo riscontrando un’interruzione del servizio. La conversazione potrebbe non funzionare fino alla risoluzione. Riprova più tardi.',
            6: 'Estamos com uma interrupção no serviço. Sua conversa pode não funcionar até que seja resolvida. Tente novamente mais tarde.',
            7: 'Er is momenteel een storing in de dienst. Uw gesprek werkt mogelijk niet totdat dit is opgelost. Probeer het later opnieuw.',
            8: 'В настоящее время наблюдается сбой в работе сервиса. Ваш разговор может не работать до его устранения. Пожалуйста, попробуйте позже.',
            9: '我们目前遇到服务中断。在问题解决之前，您的对话可能无法使用。请稍后再试。',
            10: '現在サービス障害が発生しています。復旧するまで会話が利用できない場合があります。しばらくしてからもう一度お試しください。',
            11: '현재 서비스 장애가 발생했습니다. 복구될 때까지 대화가 작동하지 않을 수 있습니다. 잠시 후 다시 시도해 주세요.',
            12: 'نواجه حاليًا انقطاعًا في الخدمة. قد لا تعمل محادثتك حتى يتم حل المشكلة. يُرجى المحاولة لاحقًا.',
            13: 'हम वर्तमान में सेवा में रुकावट का सामना कर रहे हैं। समाधान होने तक आपकी बातचीत काम नहीं कर सकती है। कृपया बाद में पुनः प्रयास करें।',
            14: 'Meil on praegu teenusekatkestus. Vestlus ei pruugi enne selle lahendamist töötada. Palun proovi hiljem uuesti.',
            15: 'Palvelussa on tällä hetkellä häiriö. Keskustelu ei ehkä toimi ennen kuin se on korjattu. Yritä myöhemmin uudelleen.',
            16: 'Vi har just nu ett driftavbrott. Din konversation kanske inte fungerar förrän det är åtgärdat. Försök igen senare.',
            17: 'Vi har for tiden et driftsavbrudd. Samtalen din fungerer kanskje ikke før det er løst. Prøv igjen senere.',
            18: 'Vi har i øjeblikket et driftsnedbrud. Din samtale fungerer muligvis ikke, før det er løst. Prøv igen senere.',
            19: 'Aktuálně dochází k výpadku služby. Vaše konverzace nemusí fungovat, dokud nebude vyřešen. Zkuste to prosím později.',
            20: 'Jelenleg szolgáltatáskimaradás áll fenn. A beszélgetés a hiba elhárításáig nem biztos, hogy működik. Kérjük, próbálja újra később.',
            21: 'Obecnie występuje awaria usługi. Twoja rozmowa może nie działać do czasu jej usunięcia. Spróbuj ponownie później.',
            22: 'În prezent avem o întrerupere a serviciului. Conversația dvs. poate să nu funcționeze până la remediere. Vă rugăm să încercați mai târziu.',
            23: 'Şu anda bir hizmet kesintisi yaşıyoruz. Sorun giderilene kadar görüşmeniz çalışmayabilir. Lütfen daha sonra tekrar deneyin.',
            24: 'ขณะนี้ระบบของเราขัดข้อง การสนทนาของคุณอาจใช้งานไม่ได้จนกว่าจะแก้ไขเสร็จ โปรดลองอีกครั้งในภายหลัง',
            25: 'Hiện tại chúng tôi đang gặp sự cố dịch vụ. Cuộc trò chuyện của bạn có thể không hoạt động cho đến khi sự cố được khắc phục. Vui lòng thử lại sau.',
            26: 'Saat ini kami mengalami gangguan layanan. Percakapan Anda mungkin tidak berfungsi sampai masalah teratasi. Silakan coba lagi nanti.',
            27: 'Kasalukuyan kaming may service outage. Maaaring hindi gumana ang iyong pag-uusap hangga’t hindi naaayos ito. Pakisubukang muli mamaya.',
            28: 'Šiuo metu vyksta paslaugos trikdis. Jūsų pokalbis gali neveikti, kol jis nebus išspręstas. Bandykite vėliau.',
            29: 'Šobrīd ir pakalpojuma darbības traucējumi. Jūsu saruna var nedarboties, līdz tie tiks novērsti. Lūdzu, mēģiniet vēlāk.',
            30: 'В момента има прекъсване на услугата. Разговорът ви може да не работи, докато проблемът не бъде отстранен. Моля, опитайте по-късно.',
            31: 'Momentálne dochádza k výpadku služby. Vaša konverzácia môže nefungovať, kým sa nevyrieši. Skúste to prosím neskôr.',
            32: 'Trenutno imamo motnjo v delovanju storitve. Vaš pogovor morda ne bo deloval, dokler je ne odpravimo. Poskusite znova pozneje.',
            33: 'Trenutno imamo prekid usluge. Vaš razgovor možda neće raditi dok se to ne riješi. Molimo pokušajte ponovo kasnije.',
            34: 'Тренутно имамо прекид услуге. Ваш разговор можда неће радити док се то не реши. Покушајте поново касније.',
            35: 'Зараз у нас збій у роботі сервісу. Ваша розмова може не працювати до його усунення. Будь ласка, спробуйте пізніше.',
            36: 'Моментално имаме прекин на услугата. Вашиот разговор може да не работи додека не се реши. Ве молиме обидете се подоцна.',
            37: 'Trenutno imamo prekid usluge. Vaš razgovor možda neće raditi dok se to ne riješi. Molimo pokušajte ponovo kasnije.',
            38: 'Aktualisht kemi një ndërprerje të shërbimit. Biseda juaj mund të mos funksionojë derisa problemi të zgjidhet. Provoni përsëri më vonë.',
            39: 'Estamos com uma interrupção no serviço. Sua conversa pode não funcionar até que seja resolvida. Tente novamente mais tarde.',
          },
        },
        input_mic_error: {
          values: {
            1: 'Microphone permission is required to record audio.',
            2: 'Se requiere permiso de micrófono para grabar audio.',
            3: "L'autorisation du microphone est requise pour enregistrer l'audio.",
            4: 'Mikrofonberechtigung ist erforderlich, um Audio aufzunehmen.',
            5: "È necessaria l'autorizzazione del microfono per registrare l'audio.",
            6: 'A permissão do microfone é necessária para gravar áudio.',
            7: 'Microfoonmachtiging is vereist om audio op te nemen.',
            8: 'Для записи звука требуется разрешение на использование микрофона.',
            9: '录制音频需要麦克风权限。',
            10: '音声を録音するにはマイクの許可が必要です。',
            11: '오디오를 녹음하려면 마이크 권한이 필요합니다.',
            12: 'مطلوب إذن الميكروفون لتسجيل الصوت.',
            13: 'ऑडियो रिकॉर्ड करने के लिए माइक्रोफ़ोन की अनुमति आवश्यक है।',
            14: 'Heli salvestamiseks on vaja mikrofoni luba.',
            15: 'Äänen tallentamiseen tarvitaan mikrofonin käyttöoikeus.',
            16: 'Mikrofonbehörighet krävs för att spela in ljud.',
            17: 'Mikrofontillatelse kreves for å ta opp lyd.',
            18: 'Mikrofontilladelse er påkrævet for at optage lyd.',
            19: 'K nahrávání zvuku je vyžadováno povolení mikrofonu.',
            20: 'A hangrögzítéshez mikrofonengedély szükséges.',
            21: 'Do nagrywania dźwięku wymagane jest uprawnienie do mikrofonu.',
            22: 'Este necesară permisiunea microfonului pentru a înregistra audio.',
            23: 'Ses kaydı için mikrofon izni gereklidir.',
            24: 'ต้องได้รับอนุญาตให้ใช้ไมโครโฟนเพื่อบันทึกเสียง',
            25: 'Cần có quyền truy cập micrô để ghi âm.',
            26: 'Izin mikrofon diperlukan untuk merekam audio.',
            27: 'Kinakailangan ang pahintulot ng mikropono upang mag-record ng audio.',
            28: 'Norint įrašyti garsą, reikalingas mikrofono leidimas.',
            29: 'Lai ierakstītu audio, nepieciešama mikrofona atļauja.',
            30: 'За запис на звук е необходимо разрешение за микрофона.',
            31: 'Na nahrávanie zvuku sa vyžaduje povolenie mikrofónu.',
            32: 'Za snemanje zvoka je potrebno dovoljenje za mikrofon.',
            33: 'Za snimanje zvuka potrebna je dozvola za mikrofon.',
            34: 'За снимање звука потребна је дозвола за микрофон.',
            35: 'Для запису звуку потрібен дозвіл на використання мікрофона.',
            36: 'Потребна е дозвола за микрофон за снимање аудио.',
            37: 'Za snimanje zvuka potrebna je dozvola za mikrofon.',
            38: 'Kërkohet leja e mikrofonit për të regjistruar audio.',
            39: 'É necessária permissão do microfone para gravar áudio.',
          },
        },
        input_audio_too_large: {
          values: {
            1: 'Audio message is too large. Maximum size is {max} MB.',
            2: 'El mensaje de audio es demasiado grande. El tamaño máximo es de {max} MB.',
            3: 'Le message audio est trop volumineux. La taille maximale est de {max} Mo.',
            4: 'Die Audionachricht ist zu groß. Die maximale Größe beträgt {max} MB.',
            5: 'Il messaggio audio è troppo grande. La dimensione massima è {max} MB.',
            6: 'A mensagem de áudio é muito grande. O tamanho máximo é de {max} MB.',
            7: 'Het audiobericht is te groot. De maximale grootte is {max} MB.',
            8: 'Голосовое сообщение слишком большое. Максимальный размер: {max} МБ.',
            9: '语音消息过大。最大大小为 {max} MB。',
            10: '音声メッセージが大きすぎます。最大サイズは {max} MB です。',
            11: '음성 메시지가 너무 큽니다. 최대 크기는 {max}MB입니다.',
            12: 'الرسالة الصوتية كبيرة جدًا. الحد الأقصى للحجم هو {max} ميجابايت.',
            13: 'ऑडियो संदेश बहुत बड़ा है। अधिकतम आकार {max} MB है।',
            14: 'Helisõnum on liiga suur. Maksimaalne suurus on {max} MB.',
            15: 'Ääniviesti on liian suuri. Enimmäiskoko on {max} Mt.',
            16: 'Röstmeddelandet är för stort. Maximal storlek är {max} MB.',
            17: 'Talemeldingen er for stor. Maksimal størrelse er {max} MB.',
            18: 'Talebeskeden er for stor. Den maksimale størrelse er {max} MB.',
            19: 'Hlasová zpráva je příliš velká. Maximální velikost je {max} MB.',
            20: 'A hangüzenet túl nagy. A maximális méret {max} MB.',
            21: 'Wiadomość głosowa jest za duża. Maksymalny rozmiar to {max} MB.',
            22: 'Mesajul audio este prea mare. Dimensiunea maximă este de {max} MB.',
            23: 'Sesli mesaj çok büyük. Maksimum boyut {max} MB.',
            24: 'ข้อความเสียงมีขนาดใหญ่เกินไป ขนาดสูงสุดคือ {max} MB',
            25: 'Tin nhắn thoại quá lớn. Kích thước tối đa là {max} MB.',
            26: 'Pesan suara terlalu besar. Ukuran maksimum adalah {max} MB.',
            27: 'Masyadong malaki ang mensahe ng boses. Ang maximum na laki ay {max} MB.',
            28: 'Balso žinutė per didelė. Didžiausias dydis yra {max} MB.',
            29: 'Balss ziņa ir pārāk liela. Maksimālais izmērs ir {max} MB.',
            30: 'Гласовото съобщение е твърде голямо. Максималният размер е {max} MB.',
            31: 'Hlasová správa je príliš veľká. Maximálna veľkosť je {max} MB.',
            32: 'Glasovno sporočilo je preveliko. Največja velikost je {max} MB.',
            33: 'Glasovna poruka je prevelika. Maksimalna veličina je {max} MB.',
            34: 'Гласовна порука је превелика. Максимална величина је {max} MB.',
            35: 'Голосове повідомлення завелике. Максимальний розмір: {max} MB.',
            36: 'Гласовната порака е преголема. Максималната големина е {max} MB.',
            37: 'Glasovna poruka je prevelika. Maksimalna veličina je {max} MB.',
            38: 'Mesazhi zanor është shumë i madh. Madhësia maksimale është {max} MB.',
            39: 'A mensagem de áudio é muito grande. O tamanho máximo é de {max} MB.',
          },
        },
        input_audio_max_duration_reached: {
          values: {
            1: 'Max recording length reached ({duration}). You can record another message.',
            2: 'Se alcanzó la duración máxima de grabación ({duration}). Puede grabar otro mensaje.',
            3: "La durée maximale d'enregistrement est atteinte ({duration}). Vous pouvez enregistrer un autre message.",
            4: 'Die maximale Aufnahmedauer wurde erreicht ({duration}). Sie können eine weitere Nachricht aufnehmen.',
            5: 'È stata raggiunta la durata massima di registrazione ({duration}). Puoi registrare un altro messaggio.',
            6: 'A duração máxima da gravação foi atingida ({duration}). Você pode gravar outra mensagem.',
            7: 'De maximale opnamelengte is bereikt ({duration}). U kunt nog een bericht opnemen.',
            8: 'Достигнута максимальная длительность записи ({duration}). Вы можете записать еще одно сообщение.',
            9: '已达到最大录音时长（{duration}）。您可以再录制一条消息。',
            10: '録音の最大時間に達しました（{duration}）。別のメッセージを録音できます。',
            11: '최대 녹음 길이에 도달했습니다({duration}). 다른 메시지를 녹음할 수 있습니다.',
            12: 'تم الوصول إلى الحد الأقصى لمدة التسجيل ({duration}). يمكنك تسجيل رسالة أخرى.',
            13: 'रिकॉर्डिंग की अधिकतम अवधि ({duration}) तक पहुंच गई है। आप एक और संदेश रिकॉर्ड कर सकते हैं।',
            14: 'Maksimaalne salvestuse pikkus on saavutatud ({duration}). Saate salvestada veel ühe sõnumi.',
            15: 'Tallennuksen enimmäiskesto on saavutettu ({duration}). Voit tallentaa toisen viestin.',
            16: 'Maximal inspelningslängd har uppnåtts ({duration}). Du kan spela in ett nytt meddelande.',
            17: 'Maksimal opptakslengde er nådd ({duration}). Du kan ta opp en ny melding.',
            18: 'Maksimal optagelseslængde er nået ({duration}). Du kan optage en ny besked.',
            19: 'Byla dosažena maximální délka nahrávání ({duration}). Můžete nahrát další zprávu.',
            20: 'Elérte a maximális felvételi időt ({duration}). Felvehet egy újabb üzenetet.',
            21: 'Osiągnięto maksymalną długość nagrania ({duration}). Możesz nagrać kolejną wiadomość.',
            22: 'A fost atinsă durata maximă de înregistrare ({duration}). Puteți înregistra un alt mesaj.',
            23: 'Maksimum kayıt süresine ulaşıldı ({duration}). Başka bir mesaj kaydedebilirsiniz.',
            24: 'ถึงความยาวการบันทึกสูงสุดแล้ว ({duration}) คุณสามารถบันทึกข้อความอีกข้อความได้',
            25: 'Đã đạt thời lượng ghi âm tối đa ({duration}). Bạn có thể ghi âm thêm một tin nhắn.',
            26: 'Durasi rekaman maksimum telah tercapai ({duration}). Anda dapat merekam pesan lain.',
            27: 'Naabot na ang maximum na haba ng pag-record ({duration}). Maaari kang mag-record ng isa pang mensahe.',
            28: 'Pasiektas maksimalus įrašymo ilgis ({duration}). Galite įrašyti dar vieną žinutę.',
            29: 'Sasniegts maksimālais ieraksta ilgums ({duration}). Varat ierakstīt vēl vienu ziņu.',
            30: 'Достигната е максималната продължителност на записа ({duration}). Можете да запишете още едно съобщение.',
            31: 'Bola dosiahnutá maximálna dĺžka nahrávania ({duration}). Môžete nahrať ďalšiu správu.',
            32: 'Dosežena je največja dolžina snemanja ({duration}). Posnamete lahko še eno sporočilo.',
            33: 'Dostignuta je maksimalna dužina snimanja ({duration}). Možete snimiti još jednu poruku.',
            34: 'Достигнута је максимална дужина снимања ({duration}). Можете снимити још једну поруку.',
            35: 'Досягнуто максимальної тривалості запису ({duration}). Ви можете записати ще одне повідомлення.',
            36: 'Достигнато е максималното времетраење на снимање ({duration}). Можете да снимите уште една порака.',
            37: 'Dostignuta je maksimalna dužina snimanja ({duration}). Možete snimiti još jednu poruku.',
            38: 'U arrit gjatësia maksimale e regjistrimit ({duration}). Mund të regjistroni një mesazh tjetër.',
            39: 'Duração máxima de gravação atingida ({duration}). Você pode gravar outra mensagem.',
          },
        },
        audio_unavailable_session: {
          values: {
            1: 'Audio file is no longer available in this session.',
            2: 'El archivo de audio ya no está disponible en esta sesión.',
            3: 'Le fichier audio n’est plus disponible dans cette session.',
            4: 'Die Audiodatei ist in dieser Sitzung nicht mehr verfügbar.',
            5: 'Il file audio non è più disponibile in questa sessione.',
            6: 'O arquivo de áudio não está mais disponível nesta sessão.',
            7: 'Het audiobestand is niet langer beschikbaar in deze sessie.',
            8: 'Аудиофайл больше недоступен в этой сессии.',
            9: '音频文件在此会话中已不可用。',
            10: 'このセッションでは音声ファイルは利用できなくなりました。',
            11: '이 세션에서 오디오 파일을 더 이상 사용할 수 없습니다.',
            12: 'لم يعد ملف الصوت متاحًا في هذه الجلسة.',
            13: 'इस सत्र में ऑडियो फ़ाइल अब उपलब्ध नहीं है।',
            14: 'Helifail ei ole selles seansis enam saadaval.',
            15: 'Äänitiedosto ei ole enää käytettävissä tässä istunnossa.',
            16: 'Ljudfilen är inte längre tillgänglig i den här sessionen.',
            17: 'Lydfilen er ikke lenger tilgjengelig i denne økten.',
            18: 'Lydfilen er ikke længere tilgængelig i denne session.',
            19: 'Zvukový soubor již není v této relaci dostupný.',
            20: 'A hangfájl ebben a munkamenetben már nem érhető el.',
            21: 'Plik audio nie jest już dostępny w tej sesji.',
            22: 'Fișierul audio nu mai este disponibil în această sesiune.',
            23: 'Ses dosyası bu oturumda artık kullanılamıyor.',
            24: 'ไฟล์เสียงไม่พร้อมใช้งานในเซสชันนี้อีกต่อไป',
            25: 'Tệp âm thanh không còn khả dụng trong phiên này.',
            26: 'File audio tidak lagi tersedia dalam sesi ini.',
            27: 'Hindi na available ang audio file sa session na ito.',
            28: 'Garso failas šioje sesijoje nebepasiekiamas.',
            29: 'Audio fails šajā sesijā vairs nav pieejams.',
            30: 'Аудио файлът вече не е наличен в тази сесия.',
            31: 'Zvukový súbor už v tejto relácii nie je dostupný.',
            32: 'Zvočna datoteka v tej seji ni več na voljo.',
            33: 'Audio datoteka više nije dostupna u ovoj sesiji.',
            34: 'Аудио датотека више није доступна у овој сесији.',
            35: 'Аудіофайл більше недоступний у цій сесії.',
            36: 'Аудио-датотеката повеќе не е достапна во оваа сесија.',
            37: 'Audio datoteka više nije dostupna u ovoj sesiji.',
            38: 'Skedari audio nuk është më i disponueshëm në këtë sesion.',
            39: 'O arquivo de áudio não está mais disponível nesta sessão.',
          },
        },
        file_unavailable_session: {
          values: {
            1: 'File is no longer available in this session.',
            2: 'El archivo ya no está disponible en esta sesión.',
            3: 'Le fichier n\u2019est plus disponible dans cette session.',
            4: 'Die Datei ist in dieser Sitzung nicht mehr verfügbar.',
            5: 'Il file non è più disponibile in questa sessione.',
            6: 'O arquivo não está mais disponível nesta sessão.',
            7: 'Het bestand is niet langer beschikbaar in deze sessie.',
            8: 'Файл больше недоступен в этой сессии.',
            9: '文件在此会话中已不可用。',
            10: 'このセッションではファイルは利用できなくなりました。',
            11: '이 세션에서 파일을 더 이상 사용할 수 없습니다.',
            12: 'لم يعد الملف متاحًا في هذه الجلسة.',
            13: 'इस सत्र में फ़ाइल अब उपलब्ध नहीं है।',
            14: 'Fail ei ole selles seansis enam saadaval.',
            15: 'Tiedosto ei ole enää käytettävissä tässä istunnossa.',
            16: 'Filen är inte längre tillgänglig i den här sessionen.',
            17: 'Filen er ikke lenger tilgjengelig i denne økten.',
            18: 'Filen er ikke længere tilgængelig i denne session.',
            19: 'Soubor již není v této relaci dostupný.',
            20: 'A fájl ebben a munkamenetben már nem érhető el.',
            21: 'Plik nie jest już dostępny w tej sesji.',
            22: 'Fișierul nu mai este disponibil în această sesiune.',
            23: 'Dosya bu oturumda artık kullanılamıyor.',
            24: 'ไฟล์ไม่พร้อมใช้งานในเซสชันนี้อีกต่อไป',
            25: 'Tệp không còn khả dụng trong phiên này.',
            26: 'File tidak lagi tersedia dalam sesi ini.',
            27: 'Hindi na available ang file sa session na ito.',
            28: 'Failas šioje sesijoje nebepasiekiamas.',
            29: 'Fails šajā sesijā vairs nav pieejams.',
            30: 'Файлът вече не е наличен в тази сесия.',
            31: 'Súbor už v tejto relácii nie je dostupný.',
            32: 'Datoteka v tej seji ni več na voljo.',
            33: 'Datoteka više nije dostupna u ovoj sesiji.',
            34: 'Датотека више није доступна у овој сесији.',
            35: 'Файл більше недоступний у цій сесії.',
            36: 'Датотеката повеќе не е достапна во оваа сесија.',
            37: 'Datoteka više nije dostupna u ovoj sesiji.',
            38: 'Skedari nuk është më i disponueshëm në këtë sesion.',
            39: 'O arquivo não está mais disponível nesta sessão.',
          },
        },
        default_thread_title: {
          values: {
            1: 'Conversation',
            2: 'Conversación',
            3: 'Conversation',
            4: 'Konversation',
            5: 'Conversazione',
            6: 'Conversa',
            7: 'Gesprek',
            8: 'Разговор',
            9: '对话',
            10: '会話',
            11: '대화',
            12: 'محادثة',
            13: 'बातचीत',
            14: 'Vestlus',
            15: 'Keskustelu',
            16: 'Konversation',
            17: 'Samtale',
            18: 'Samtale',
            19: 'Konverzace',
            20: 'Beszélgetés',
            21: 'Rozmowa',
            22: 'Conversație',
            23: 'Görüşme',
            24: 'การสนทนา',
            25: 'Cuộc trò chuyện',
            26: 'Percakapan',
            27: 'Pag-uusap',
            28: 'Pokalbis',
            29: 'Saruna',
            30: 'Разговор',
            31: 'Konverzácia',
            32: 'Pogovor',
            33: 'Razgovor',
            34: 'Разговор',
            35: 'Розмова',
            36: 'Разговор',
            37: 'Razgovor',
            38: 'Bisedë',
            39: 'Conversa',
          },
        },
        processing_label_audio_analysis: {
          values: {
            1: 'Reviewing audio',
            2: 'Revisando audio',
            3: 'Examen de l’audio en cours',
            4: 'Audio wird überprüft',
            5: 'Revisione dell’audio in corso',
            6: 'Revisando áudio',
            7: 'Audio wordt gecontroleerd',
            8: 'Проверка аудио',
            9: '正在检查音频',
            10: 'オーディオを確認しています',
            11: '오디오를 검토하는 중',
            12: 'جارٍ مراجعة الصوت',
            13: 'ऑडियो की समीक्षा की जा रही है',
            14: 'Audio ülevaatamine',
            15: 'Tarkistetaan ääntä',
            16: 'Granskar ljud',
            17: 'Gjennomgår lyd',
            18: 'Gennemgår lyd',
            19: 'Kontrola zvuku',
            20: 'Hang ellenőrzése',
            21: 'Sprawdzanie dźwięku',
            22: 'Se verifică audio',
            23: 'Ses inceleniyor',
            24: 'กำลังตรวจสอบเสียง',
            25: 'Đang kiểm tra âm thanh',
            26: 'Meninjau audio',
            27: 'Sinusuri ang audio',
            28: 'Peržiūrimas garsas',
            29: 'Notiek audio pārbaude',
            30: 'Проверка на аудио',
            31: 'Kontrola zvuku',
            32: 'Pregled zvoka',
            33: 'Provjera zvuka',
            34: 'Провера звука',
            35: 'Перевірка аудіо',
            36: 'Проверка на аудио',
            37: 'Provjera zvuka',
            38: 'Po shqyrtohet audio',
            39: 'Revisando áudio',
          },
        },
      },
    },
    server_errors: {
      items: {
        invalid_token: {
          values: {
            1: 'Invalid token',
            2: 'Token inválido',
            3: 'Jeton invalide',
            4: 'Ungültiges Token',
            5: 'Token non valido',
            6: 'Token inválido',
            7: 'Ongeldig token',
            8: 'Неверный токен',
            9: '无效令牌',
            10: '無効なトークン',
            11: '유효하지 않은 토큰',
            12: 'رمز غير صالح',
            13: 'अमान्य टोकन',
            14: 'Kehtetu luba',
            15: 'Virheellinen tunniste',
            16: 'Ogiltig token',
            17: 'Ugyldig token',
            18: 'Ugyldig token',
            19: 'Neplatný token',
            20: 'Érvénytelen token',
            21: 'Nieprawidłowy token',
            22: 'Token invalid',
            23: 'Geçersiz belirteç',
            24: 'โทเค็นไม่ถูกต้อง',
            25: 'Mã thông báo không hợp lệ',
            26: 'Token tidak valid',
            27: 'Hindi wastong token',
            28: 'Neteisingas raktas',
            29: 'Nederīgs marķieris',
            30: 'Невалиден токен',
            31: 'Neplatný token',
            32: 'Neveljaven žeton',
            33: 'Neispravan token',
            34: 'Неважећи токен',
            35: 'Недійсний токен',
            36: 'Неважечки токен',
            37: 'Neispravan token',
            38: 'Shenjë e pavlefshme',
            39: 'Token inválido',
          },
        },
        chat_not_found: {
          values: {
            1: 'Chat not found {token}',
            2: 'Chat no encontrado {token}',
            3: 'Chat introuvable {token}',
            4: 'Chat nicht gefunden {token}',
            5: 'Chat non trovato {token}',
            6: 'Bate-papo não encontrado {token}',
            7: 'Chat niet gevonden {token}',
            8: 'Чат не найден {token}',
            9: '未找到聊天 {token}',
            10: 'チャットが見つかりません {token}',
            11: '채팅을 찾을 수 없습니다 {token}',
            12: 'لم يتم العثور على الدردشة {token}',
            13: 'चैट नहीं मिला {token}',
            14: 'Vestlust ei leitud {token}',
            15: 'Keskustelua ei löytynyt {token}',
            16: 'Chat hittades inte {token}',
            17: 'Chat ikke funnet {token}',
            18: 'Chat ikke fundet {token}',
            19: 'Chat nebyl nalezen {token}',
            20: 'Beszélgetés nem található {token}',
            21: 'Nie znaleziono czatu {token}',
            22: 'Chatul nu a fost găsit {token}',
            23: 'Sohbet bulunamadı {token}',
            24: 'ไม่พบการแชท {token}',
            26: 'Obrolan tidak ditemukan {token}',
            27: 'Hindi nakita ang chat {token}',
            28: 'Pokalbis nerastas {token}',
            29: 'Tērzēšana nav atrasta {token}',
            30: 'Чатът не е намерен {token}',
            31: 'Chat sa nenašiel {token}',
            32: 'Klepet ni najden {token}',
            33: 'Chat nije pronađen {token}',
            34: 'Ћаскање није пронађено {token}',
            35: 'Чат не знайдено {token}',
            36: 'Разговорот не е пронајден {token}',
            37: 'Chat nije pronađen {token}',
            38: 'Biseda nuk u gjet {token}',
            39: 'Chat não encontrado {token}',
          },
        },
        session_not_found: {
          values: {
            1: 'No chat session found for this token.',
            2: 'No se encontró ninguna sesión de chat para este token.',
            3: 'Aucune session de chat trouvée pour ce jeton.',
            4: 'Für dieses Token wurde keine Chat-Sitzung gefunden.',
            5: 'Nessuna sessione di chat trovata per questo token.',
            6: 'Nenhuma sessão de bate-papo encontrada para este token.',
            7: 'Geen chatsessie gevonden voor dit token.',
            8: 'Для этого токена не найдена сессия чата.',
            9: '未找到此令牌的聊天会话。',
            10: 'このトークンのチャットセッションが見つかりません。',
            11: '이 토큰에 대한 채팅 세션을 찾을 수 없습니다.',
            12: 'لم يتم العثور على جلسة دردشة لهذا الرمز.',
            13: 'इस टोकन के लिए कोई चैट सत्र नहीं मिला।',
            14: 'Selle loa jaoks ei leitud vestlusseanssi.',
            15: 'Tälle tunnisteelle ei löytynyt keskusteluistuntoa.',
            16: 'Ingen chattsession hittades för denna token.',
            17: 'Ingen chatsesjon funnet for denne tokenen.',
            18: 'Ingen chatsession fundet for dette token.',
            19: 'Pro tento token nebyla nalezena žádná relace chatu.',
            20: 'Ehhez a tokenhez nem található csevegési munkamenet.',
            21: 'Nie znaleziono sesji czatu dla tego tokena.',
            22: 'Nu a fost găsită nicio sesiune de chat pentru acest token.',
            23: 'Bu belirteç için sohbet oturumu bulunamadı.',
            24: 'ไม่พบเซสชันการแชทสำหรับโทเค็นนี้',
            25: 'Không tìm thấy phiên trò chuyện cho mã thông báo này.',
            26: 'Sesi obrolan tidak ditemukan untuk token ini.',
            27: 'Walang nakitang session ng chat para sa token na ito.',
            28: 'Šiam žetonui nerasta pokalbio sesija.',
            29: 'Šim marķierim nav atrasta tērzēšanas sesija.',
            30: 'Няма намерена чат сесия за този токен.',
            31: 'Pre tento token sa nenašla žiadna relácia chatu.',
            32: 'Za ta žeton ni bila najdena nobena klepetalna seja.',
            33: 'Nije pronađena sesija chata za ovaj token.',
            34: 'Није пронађена сесија ћаскања за овај токен.',
            35: 'Сесію чату для цього токена не знайдено.',
            36: 'Не е пронајдена сесија за разговор за овој токен.',
            37: 'Nije pronađena sesija chata za ovaj token.',
            38: 'Nuk u gjet asnjë sesion bisede për këtë shenjë.',
            39: 'Nenhuma sessão de chat encontrada para este token.',
          },
        },
        only_webchat: {
          values: {
            1: 'Chat is only supported for contact forms with WebChat channel selected.',
            2: 'El chat solo es compatible con formularios de contacto con el canal WebChat seleccionado.',
            3: 'Le chat n’est pris en charge que pour les formulaires de contact avec le canal WebChat sélectionné.',
            4: 'Chat wird nur für Kontaktformulare mit ausgewähltem WebChat-Kanal unterstützt.',
            5: 'La chat è supportata solo per i moduli di contatto con canale WebChat selezionato.',
            6: 'O bate-papo é suportado apenas para formulários de contato com canal WebChat selecionado.',
            7: 'Chat wordt alleen ondersteund voor contactformulieren waarbij het WebChat-kanaal is geselecteerd.',
            8: 'Чат поддерживается только для контактных форм с выбранным каналом WebChat.',
            9: '仅当选择了 WebChat 渠道时，联系表单才支持聊天。',
            10: 'チャットは、WebChatチャネルが選択されたコンタクトフォームでのみサポートされています。',
            11: '채팅은 WebChat 채널이 선택된 문의 양식에서만 지원됩니다.',
            12: 'الدردشة مدعومة فقط لنماذج الاتصال مع تحديد قناة WebChat.',
            13: 'चैट केवल उन संपर्क फ़ॉर्म के लिए समर्थित है जिनमें WebChat चैनल चुना गया हो।',
            14: 'Vestlus on toetatud ainult kontakvormide puhul, kui on valitud WebChat kanal.',
            15: 'Keskustelua tuetaan vain yhteydenottolomakkeissa, joissa WebChat-kanava on valittu.',
            16: 'Chat stöds endast för kontaktformulär med WebChat-kanalen vald.',
            17: 'Chat støttes kun for kontaktskjemaer med WebChat-kanal valgt.',
            18: 'Chat understøttes kun for kontaktformularer med WebChat-kanal valgt.',
            19: 'Chat je podporován pouze pro kontaktní formuláře s vybraným kanálem WebChat.',
            20: 'A csevegés csak a kiválasztott WebChat-csatornával rendelkező kapcsolatfelvételi űrlapoknál támogatott.',
            21: 'Czat jest obsługiwany tylko dla formularzy kontaktowych z wybranym kanałem WebChat.',
            22: 'Chat-ul este acceptat doar pentru formularele de contact cu canalul WebChat selectat.',
            23: 'Sohbet yalnızca WebChat kanalı seçili iletişim formları için desteklenir.',
            24: 'รองรับการแชทสำหรับแบบฟอร์มติดต่อที่เลือกช่องทาง WebChat เท่านั้น',
            25: 'Trò chuyện chỉ được hỗ trợ cho các biểu mẫu liên hệ đã chọn kênh WebChat.',
            26: 'Obrolan hanya didukung untuk formulir kontak dengan saluran WebChat yang dipilih.',
            27: 'Ang chat ay suportado lamang para sa mga form ng pakikipag-ugnayan na may napiling WebChat channel.',
            28: 'Pokalbiai palaikomi tik kontaktinėse formose su pasirinktu „WebChat“ kanalu.',
            29: 'Tērzēšana tiek atbalstīta tikai saziņas veidlapām ar atlasītu WebChat kanālu.',
            30: 'Чатът се поддържа само за формуляри за контакт с избран канал WebChat.',
            31: 'Chat je podporovaný iba pre kontaktné formuláre s vybraným kanálom WebChat.',
            32: 'Klepet je podprt samo za kontaktne obrazce z izbranim kanalom WebChat.',
            33: 'Chat je podržan samo za kontakt forme s odabranim WebChat kanalom.',
            34: 'Ћаскање је подржано само за контакт форме са изабраним WebChat каналом.',
            35: 'Чат підтримується лише для контактних форм із вибраним каналом WebChat.',
            36: 'Разговорот е поддржан само за контакт форми со избран WebChat канал.',
            37: 'Chat je podržan samo za kontakt forme sa odabranim WebChat kanalom.',
            38: 'Biseda mbështetet vetëm për format e kontaktit me kanalin WebChat të zgjedhur.',
            39: 'O chat é suportado apenas para formulários de contato com canal WebChat selecionado.',
          },
        },
        unauthenticated: {
          values: {
            1: 'User not authenticated.',
            2: 'Usuario no autenticado.',
            3: 'Utilisateur non authentifié.',
            4: 'Benutzer nicht authentifiziert.',
            5: 'Utente non autenticato.',
            6: 'Usuário não autenticado.',
            7: 'Gebruiker niet geverifieerd.',
            8: 'Пользователь не аутентифицирован.',
            9: '用户未通过身份验证。',
            10: 'ユーザーが認証されていません。',
            11: '사용자가 인증되지 않았습니다.',
            12: 'المستخدم غير موثق.',
            13: 'उपयोगकर्ता प्रमाणीकृत नहीं है।',
            14: 'Kasutaja ei ole audentitud.',
            15: 'Käyttäjää ei ole todennettu.',
            16: 'Användaren är inte autentiserad.',
            17: 'Bruker ikke autentisert.',
            18: 'Bruger ikke godkendt.',
            19: 'Uživatel není ověřen.',
            20: 'A felhasználó nincs hitelesítve.',
            21: 'Użytkownik nie jest uwierzytelniony.',
            22: 'Utilizatorul nu este autentificat.',
            23: 'Kullanıcı kimliği doğrulanmadı.',
            24: 'ผู้ใช้ยังไม่ได้รับการตรวจสอบสิทธิ์',
            25: 'Người dùng chưa được xác thực.',
            26: 'Pengguna tidak diautentikasi.',
            27: 'Hindi napatunayan ang gumagamit.',
            28: 'Vartotojas neautentifikuotas.',
            29: 'Lietotājs nav autentificēts.',
            30: 'Потребителят не е удостоверен.',
            31: 'Používateľ nie je overený.',
            32: 'Uporabnik ni overjen.',
            33: 'Korisnik nije autentificiran.',
            34: 'Корисник није аутентификован.',
            35: 'Користувач не автентифікований.',
            36: 'Корисникот не е автентициран.',
            37: 'Korisnik nije autentificiran.',
            38: 'Përdoruesi nuk është i vërtetuar.',
            39: 'Usuário não autenticado.',
          },
        },
      },
    },
  },
};

export function getChatConfigValue(
  groupId: string,
  itemId: string,
  localeId: number,
): string {
  const group = CHAT_CONFIG.groups[groupId];
  if (!group) return '';

  const item = group.items[itemId];
  if (!item) return '';

  const value = item.values[localeId];
  if (!value) {
    // Fallback to English (locale_id: 1) if specific locale not found
    return item.values[1] || '';
  }

  return value;
}
